import { FC, useState } from "react";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/_atoms/dialog";
import { OptionRadioGroup } from "@/components/_molecules/option-radio-group";

interface DatatableCsvExportButtonProps {
  onExport: (decimalSeparator: "comma" | "dot") => void | Promise<void>;
  isDisabled?: boolean;
  exportLabel?: string;
  successMessage?: string;
  errorMessage?: string;
  displayMode?: "menu" | "toolbar";
}

export const DatatableCsvExportButton: FC<DatatableCsvExportButtonProps> = ({
  onExport,
  isDisabled = false,
  exportLabel,
  successMessage,
  errorMessage,
  displayMode = "menu",
}) => {
  const { t } = useTranslation("common");
  const defaultExportLabel = t("view.csvExport.export");
  const defaultSuccessMessage = t("view.csvExport.exportSuccess");
  const defaultErrorMessage = t("view.csvExport.exportFailed");

  const [isCsvOptionsDialogOpen, setIsCsvOptionsDialogOpen] = useState(false);
  const [decimalSeparator, setDecimalSeparator] = useState<"comma" | "dot">("comma");
  const [isExporting, setIsExporting] = useState(false);

  const buttonClassName =
    displayMode === "toolbar"
      ? "font-normal"
      : "h-6 w-full justify-start overflow-x-hidden border text-xs font-normal ring-offset-background focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus";
  const iconClassName =
    displayMode === "toolbar" ? "mr-2 h-3.5 w-3.5 text-foreground/60" : "mr-2 h-4 w-5 min-w-5 shrink-0 text-foreground/60";

  const handleExportCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await onExport(decimalSeparator);
      toast.success(t("view.csvExport.exportSuccess"), { description: successMessage ?? defaultSuccessMessage });
      setIsCsvOptionsDialogOpen(false);
    } catch (error) {
      console.error("CSV export failed:", error);
      toast.error(t("view.csvExport.exportFailed"), { description: errorMessage ?? defaultErrorMessage });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="xs"
        disabled={isDisabled}
        aria-label={t("view.csvExport.exportAsCsv")}
        onClick={() => setIsCsvOptionsDialogOpen(true)}
        className={buttonClassName}
      >
        <Download className={iconClassName} />
        {exportLabel ?? defaultExportLabel}
      </Button>

      <Dialog open={isCsvOptionsDialogOpen} onOpenChange={setIsCsvOptionsDialogOpen}>
        <DialogContent className="sm:max-w-[340px]">
          <DialogHeader>
            <DialogTitle className="text-base font-medium">{t("view.csvExport.title")}</DialogTitle>
            <DialogDescription>{t("view.csvExport.description")}</DialogDescription>
          </DialogHeader>

          <OptionRadioGroup
            value={decimalSeparator}
            onValueChange={(value) => setDecimalSeparator(value as "comma" | "dot")}
            options={[
              { value: "comma", label: t("view.csvExport.comma") },
              { value: "dot", label: t("view.csvExport.dot") },
            ]}
          />

          <DialogFooter className="mt-2 flex flex-row items-center justify-end gap-2">
            <Button variant="outline" className="h-9 px-3" onClick={() => setIsCsvOptionsDialogOpen(false)}>
              {t("actions.cancel")}
            </Button>
            <Button className="h-9 px-3" disabled={isExporting} onClick={() => void handleExportCsv()}>
              {t("view.csvExport.export")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
